import { Popover, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useWidgetControllers, useWidgetData } from "../Contexts/dashboardHooks";
import { sliceColor } from "../Processing/controllerBindings";
import { getWidgetDef } from "../widgetRegistry";
import { SlicerBadgeDiscs } from "../WidgetComponents/ConnectedSlicerBadges";

const { Text } = Typography;

/**
 * The single info-popover trigger shared by every widget header, plus the slicer badge
 * discs just right of it. It resolves the widget's `info` handler from its definition
 * and renders the hover popover; a widget that opts out with `NoInfo` (or has no data
 * yet) renders no icon — but a widget that follows a slicer still renders its discs in
 * the same header slot.
 *
 * When the widget follows a slicer, the popover opens with "Controlled by
 * Slicer: <name>" above the regular rows; the icon itself stays neutral — the
 * discs beside it carry the color cue.
 */
export function WidgetInfoButton({ id }: { id: string }) {
  const data = useWidgetData(id);
  const controllers = useWidgetControllers(id);
  // `"Content" in info` discriminates a real InfoHandler from the NoInfo opt-out sentinel.
  const info = data ? getWidgetDef(data.type)?.info : undefined;
  const Content = info && "Content" in info ? info.Content : null;
  const linked = controllers.length > 0;
  if (!Content && !linked) return null;
  const content = Content && (
    <>
      {linked && (
        <div className="widget-info-slicer-line">
          <Text style={{ fontSize: 12 }}>
            Controlled by Slicer{controllers.length > 1 ? "s" : ""}:
          </Text>
          {controllers.map((c) => (
            <span key={c.uuid} className="widget-info-slicer-name">
              <span
                className="slicer-badge-disc"
                style={{ background: sliceColor(c.spec.slicer_builder_misc) }}
              />
              <Text strong style={{ fontSize: 12 }}>{c.label}</Text>
            </span>
          ))}
        </div>
      )}
      <Content id={id} />
    </>
  );
  return (
    <span className="widget-info-cluster">
      {Content && (
        <Popover content={content} trigger="hover" placement="bottomRight">
          <InfoCircleOutlined className="widget-info-icon" />
        </Popover>
      )}
      <SlicerBadgeDiscs controllers={controllers} />
    </span>
  );
}
